import {
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  BoxProps,
  Icon,
} from '@chakra-ui/react';
import { useCallback, useMemo } from 'react';
import {
  RiCheckboxBlankCircleLine,
  RiCheckboxCircleLine,
  RiPencilLine,
} from 'react-icons/ri';
import { LinkButton } from '../../../../../../../components/LinkButton';
import { Pagination } from '../../../../../../../components/Pagination';
import { TableFilters } from '../../../../../../../components/TableFilters';
import { OrderStatus } from '../../../../../../../models/order';
import { IOrderListItem } from '../../../../../../../services/Orders/ListOrdersService';

export interface IOrder extends IOrderListItem {
  formattedAmount: string;
  formattedClosedDate?: string;
  formattedDate: string;
}

interface IOnFilterProps {
  filter: string;
  value: string;
}

interface IOrderTableProps extends BoxProps {
  orders: IOrder[];
  onFilter?: (data: IOnFilterProps) => void;
  defaultFilterBy?: string;
  defaultSortBy?: string;
  onSort?: (sort: 'createdAt') => void;
  onOrder?: (order: 'ASC' | 'DESC') => void;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  totalPages?: number;
}

export const OrderTable = ({
  orders,
  onFilter,
  defaultFilterBy,
  defaultSortBy,
  onSort,
  onOrder,
  currentPage,
  onPageChange,
  totalPages,
  ...rest
}: IOrderTableProps): JSX.Element => {
  const handleSort = useCallback(
    (sort: string) => {
      if (onSort) {
        onSort(sort as 'createdAt');
      }
    },
    [onSort],
  );

  const filterOptions = useMemo(
    () => [
      {
        label: 'Recebedor',
        value: 'recipientName',
      },
      {
        label: 'Código',
        value: 'code',
      },
      {
        label: 'App',
        value: 'appName',
      },
    ],
    [],
  );

  const orderingOptions = useMemo(
    () => [
      {
        label: 'Data de cadastro',
        value: 'createdAt',
      },
    ],
    [],
  );

  return (
    <Box {...rest}>
      <TableFilters
        filterOptions={onFilter ? filterOptions : undefined}
        orderingOptions={orderingOptions}
        onFilter={onFilter}
        defaultFilterBy={defaultFilterBy}
        defaultSortBy={defaultSortBy}
        onSort={handleSort}
        onOrder={onOrder}
      />

      <Table colorScheme="blue" mt="4">
        <Thead>
          <Tr>
            <Th>Código</Th>
            <Th>App</Th>
            <Th>Recebedor</Th>
            <Th>Valor</Th>
            <Th>Jcoin</Th>
            <Th>Status</Th>
            <Th textAlign="center">Nota Emitida</Th>
            <Th>Data de abertura</Th>
            <Th>Data de fechamento</Th>
            <Th w="8" />
          </Tr>
        </Thead>

        <Tbody>
          {orders.map((order) => (
            <Tr key={order.paymentProviderOrderId}>
              <Td>{order.code}</Td>

              <Td>
                <Box>
                  <Text>{order.app.name}</Text>
                  <Text fontSize="sm" color="gray.500">
                    {`Ref: ${order.app.ref}`}
                  </Text>
                </Box>
              </Td>

              <Td>{order.recipient.name}</Td>

              <Td>{order.formattedAmount}</Td>

              <Td>{order.jcoins}</Td>

              <Td>{OrderStatus[order.status]}</Td>

              <Td textAlign="center">
                {order.invoiceIssued ? (
                  <Icon
                    as={RiCheckboxCircleLine}
                    fontSize="20"
                    color="green.500"
                  />
                ) : (
                  <Icon as={RiCheckboxBlankCircleLine} fontSize="20" />
                )}
              </Td>

              <Td>{order.formattedDate}</Td>

              <Td>{order.formattedClosedDate}</Td>

              <Td>
                <LinkButton
                  icon={<Icon as={RiPencilLine} fontSize="16" />}
                  to={{
                    pathname: '/orders/details',
                    state: {
                      orderId: order.paymentProviderOrderId,
                    },
                  }}
                >
                  Detalhes
                </LinkButton>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {!!currentPage && !!onPageChange && !!totalPages && totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          onPageChange={onPageChange}
          totalPages={totalPages}
        />
      )}
    </Box>
  );
};
